import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, ButtonBase, CircularProgress, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import TrainOutlinedIcon from '@mui/icons-material/TrainOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../auth/AuthContext';
import { Trip } from '../../types';
import { displayFont, elevation, radius, serifVoice } from '../../theme';
import { formatDay, shortTime } from '../../lib/plan';
import { sameTown } from '../../lib/towns';
import { coverUrls, ensureCover } from '../../lib/covers';

type TripRow = Pick<
  Trip,
  | 'trip_id'
  | 'title'
  | 'status'
  | 'destination'
  | 'themes'
  | 'start_date'
  | 'end_date'
  | 'start_place'
  | 'start_mode'
  | 'start_detail'
  | 'start_time'
  | 'end_place'
  | 'created_at'
  | 'cover_photo_path'
  | 'cover_photo_attribution'
>;
interface DayRow {
  trip_id: string;
  day_id: string;
  date: string;
  base_city: string | null;
  in_transit: boolean;
}
interface StopRow {
  trip_id: string;
  day_id: string | null;
  stop_type: string | null;
  leg_mode: string | null;
}

interface Summary {
  trip: TripRow;
  start: string | null;
  end: string | null;
  nights: number;
  route: { town: string; nights: number }[];
  candidates: number;
  daysFilled: number;
  dayCount: number;
  openChoices: number;
  daysAway: number | null;
  underway: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);
const daysBetween = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);

function summarize(trip: TripRow, days: DayRow[], stops: StopRow[]): Summary {
  const own = days.filter((d) => d.trip_id === trip.trip_id).sort((a, b) => (a.date < b.date ? -1 : 1));
  // Trips made in the mobile app have days but no start/finish yet.
  const start = trip.start_date ?? own[0]?.date ?? null;
  const end = trip.end_date ?? own[own.length - 1]?.date ?? null;
  const route: { town: string; nights: number }[] = [];
  for (const d of own.filter((x) => x.date !== end)) {
    if (!d.base_city) continue;
    const last = route[route.length - 1];
    if (last && sameTown(last.town, d.base_city)) last.nights++;
    else route.push({ town: d.base_city, nights: 1 });
  }
  const tripStops = stops.filter((s) => s.trip_id === trip.trip_id);
  const placedDays = new Set(tripStops.filter((s) => s.day_id && s.stop_type !== 'transit').map((s) => s.day_id));
  // A day spent on board has nothing to plan, so it is not waiting on you.
  const plannable = own.filter((d) => !d.in_transit || placedDays.has(d.day_id));
  const now = today();
  return {
    trip,
    start,
    end,
    nights: start && end ? Math.max(0, daysBetween(start, end)) : 0,
    route,
    candidates: tripStops.filter((s) => !s.day_id && s.stop_type !== 'transit').length,
    daysFilled: plannable.filter((d) => placedDays.has(d.day_id)).length,
    dayCount: plannable.length,
    openChoices:
      tripStops.filter((s) => s.stop_type === 'transit' && s.day_id && !s.leg_mode).length +
      (trip.start_mode ? 0 : 1),
    daysAway: start ? daysBetween(now, start) : null,
    underway: !!start && !!end && start <= now && end >= now,
  };
}

/**
 * The name the traveller gave Roamoir — the website profile first (Profile
 * page), then the mobile app's. Never guessed from an email address.
 */
async function fetchDisplayName(uid: string): Promise<string | null> {
  const [web, app] = await Promise.all([
    supabase.from('user_profiles').select('display_name').eq('user_id', uid).maybeSingle(),
    supabase.from('profiles').select('display_name').eq('user_id', uid).maybeSingle(),
  ]);
  const pick = (r: { data: unknown }) => ((r.data as { display_name?: string | null } | null)?.display_name ?? '').trim();
  return pick(web) || pick(app) || null;
}

/** Save the name in both profiles, so the website and the app agree. */
async function saveDisplayName(uid: string, name: string): Promise<string | null> {
  const [app, web] = await Promise.all([
    supabase.from('profiles').update({ display_name: name }).eq('user_id', uid),
    // Only updates an existing website profile; creating one needs a public slug.
    supabase.from('user_profiles').update({ display_name: name }).eq('user_id', uid),
  ]);
  return app.error?.message ?? web.error?.message ?? null;
}

function countdown(s: Summary): string {
  if (s.underway) return 'On the road';
  if (s.daysAway === null) return 'Planning';
  if (s.daysAway < 0) return 'Done';
  if (s.daysAway === 0) return 'Today';
  if (s.daysAway === 1) return 'Tomorrow';
  return `In ${s.daysAway} days`;
}

export default function TripsPage() {
  const { session } = useAuth();
  const [trips, setTrips] = useState<TripRow[]>([]);
  const [days, setDays] = useState<DayRow[]>([]);
  const [stops, setStops] = useState<StopRow[]>([]);
  const [covers, setCovers] = useState<Record<string, string>>({});
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    (async () => {
      const [t, name] = await Promise.all([
        supabase
          .from('trips')
          .select(
            'trip_id, title, status, destination, themes, start_date, end_date, start_place, start_mode, start_detail, start_time, end_place, created_at, cover_photo_path, cover_photo_attribution',
          )
          .is('deleted_at', null)
          .order('created_at', { ascending: false }),
        fetchDisplayName(session.user.id),
      ]);
      if (cancelled) return;
      const rows = (t.data ?? []) as TripRow[];
      setTrips(rows);
      setDisplayName(name);
      if (t.error) setError(t.error.message);
      if (rows.length) {
        const ids = rows.map((r) => r.trip_id);
        const [d, s, urls] = await Promise.all([
          supabase.from('days').select('trip_id, day_id, date, base_city, in_transit').in('trip_id', ids).is('deleted_at', null),
          supabase.from('stops').select('trip_id, day_id, stop_type, leg_mode').in('trip_id', ids).is('deleted_at', null),
          coverUrls(rows.map((r) => r.cover_photo_path)),
        ]);
        if (cancelled) return;
        setDays((d.data ?? []) as DayRow[]);
        setStops((s.data ?? []) as StopRow[]);
        setCovers(urls);
      }
      setLoading(false);

      // Trips without a cover get one in the background — older trips, and
      // ones whose cover could not be fetched when they were created.
      for (const r of rows.filter((x) => !x.cover_photo_path && (x.destination || x.start_place))) {
        const { path, attribution } = await ensureCover(r.trip_id);
        if (cancelled || !path) continue;
        const url = (await coverUrls([path]))[path];
        setTrips((prev) => prev.map((x) => (x.trip_id === r.trip_id ? { ...x, cover_photo_path: path, cover_photo_attribution: attribution } : x)));
        if (url) setCovers((prev) => ({ ...prev, [path]: url }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const { upcoming, past, next } = useMemo(() => {
    const all = trips.map((t) => summarize(t, days, stops));
    const now = today();
    const up = all.filter((s) => !s.end || s.end >= now).sort((a, b) => ((a.start ?? '9') < (b.start ?? '9') ? -1 : 1));
    const done = all.filter((s) => s.end && s.end < now).sort((a, b) => ((a.start ?? '') > (b.start ?? '') ? -1 : 1));
    return { upcoming: up, past: done, next: up[0] };
  }, [trips, days, stops]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const shown = tab === 'upcoming' ? upcoming : past;
  const coverOf = (s: Summary | undefined) => (s?.trip.cover_photo_path ? covers[s.trip.cover_photo_path] : undefined);

  return (
    <Box sx={{ maxWidth: 1240, mx: 'auto', display: 'flex', flexDirection: 'column', gap: { xs: 4, md: 7 }, pb: 6 }}>
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Hero
        name={displayName}
        onName={async (n) => {
          if (!session) return null;
          const err = await saveDisplayName(session.user.id, n);
          if (!err) setDisplayName(n);
          return err;
        }}
        next={next}
        photo={coverOf(next)}
      />

      {trips.length === 0 ? (
        <HowItWorks />
      ) : (
        <Box component="section" aria-labelledby="your-trips">
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: { xs: 2, md: 3 } }}>
            <Typography id="your-trips" component="h2" sx={{ fontFamily: displayFont, fontWeight: 700, fontSize: { xs: '1.6rem', md: '2.2rem' }, color: 'text.primary', lineHeight: 1.1 }}>
              Your trips
            </Typography>
            <Box role="tablist" aria-label="Which trips" sx={{ display: 'flex', gap: 1 }}>
              {(
                [
                  ['upcoming', `Upcoming · ${upcoming.length}`],
                  ['past', `Past · ${past.length}`],
                ] as const
              ).map(([key, label]) => (
                <ButtonBase
                  key={key}
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                  sx={{
                    px: 2,
                    height: 38,
                    borderRadius: 999,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: tab === key ? '#FFFFFF' : 'text.secondary',
                    bgcolor: tab === key ? 'primary.main' : '#FFFFFF',
                    boxShadow: tab === key ? 'none' : elevation.card,
                  }}
                >
                  {label}
                </ButtonBase>
              ))}
            </Box>
          </Box>

          {shown.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4 }}>
              {tab === 'upcoming' ? 'Nothing coming up. Start a new trip above.' : 'No past trips yet.'}
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                gap: { xs: 1.5, md: 3 },
              }}
            >
              {shown.map((s) => (
                <TripTile key={s.trip.trip_id} s={s} photo={coverOf(s)} />
              ))}
              <NewTripTile />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

/* ── Hero: greeting left, the next trip as a big photo right ─────────────── */

function Hero({
  name,
  onName,
  next,
  photo,
}: {
  name: string | null;
  onName: (name: string) => Promise<string | null>;
  next: Summary | undefined;
  photo: string | undefined;
}) {
  return (
    <Box
      component="section"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1.05fr)' },
        gap: { xs: 3, md: 6 },
        alignItems: 'center',
        pt: { xs: 1, md: 4 },
      }}
    >
      <Box>
        {name ? (
          <Typography sx={{ color: 'text.secondary', fontWeight: 600, fontSize: { xs: '0.95rem', md: '1.05rem' } }}>
            Hello, {name}
          </Typography>
        ) : (
          <NamePrompt onSave={onName} />
        )}
        <Typography
          component="h1"
          sx={{ fontFamily: displayFont, fontWeight: 700, fontSize: { xs: '2.5rem', sm: '3.2rem', md: '4.2rem' }, lineHeight: 1.04, letterSpacing: '-0.02em', mt: 1, color: 'text.primary' }}
        >
          Where are you{' '}
          <Box component="span" sx={{ fontStyle: 'italic', color: 'secondary.main', position: 'relative', whiteSpace: 'nowrap' }}>
            going next?
          </Box>
        </Typography>
        <Typography sx={{ display: { xs: 'none', sm: 'block' }, color: 'text.secondary', fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.7, mt: 2, maxWidth: 460 }}>
          Start from the dates, a list of must-sees, or a booking. Roamoir helps shape the days around them.
        </Typography>

        <ButtonBase
          component={Link}
          to="/trips/new"
          sx={{
            mt: { xs: 3, md: 4 },
            width: '100%',
            maxWidth: 520,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1,
            pl: 2.5,
            borderRadius: 999,
            bgcolor: '#FFFFFF',
            boxShadow: elevation.raised,
            justifyContent: 'flex-start',
            textAlign: 'left',
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>Plan a new trip</Typography>
            <Typography noWrap sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              Dates, a list, or a booking screenshot
            </Typography>
          </Box>
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'secondary.main', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
            <AddIcon />
          </Box>
        </ButtonBase>
        <Box sx={{ display: 'flex', gap: 2.5, mt: 2, ml: 1, flexWrap: 'wrap' }}>
          <HintLink to="/trips/new" icon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: 18 }} />} label="Fill from a booking" />
        </Box>
      </Box>

      {next ? <NextTripPhoto s={next} photo={photo} /> : <EmptyCollage />}
    </Box>
  );
}

/** Asked once, when Roamoir has no name for the traveller. */
function NamePrompt({ onSave }: { onSave: (name: string) => Promise<string | null> }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;
    setSaving(true);
    setError(await onSave(value.trim()));
    setSaving(false);
  }
  return (
    <Box component="form" onSubmit={save} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography component="label" htmlFor="call-me" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: { xs: '0.95rem', md: '1.05rem' } }}>
        Welcome back. What should Roamoir call you?
      </Typography>
      <Box
        component="input"
        id="call-me"
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
        placeholder="Your name"
        sx={{ height: 36, px: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 999, font: 'inherit', fontSize: '0.9rem', bgcolor: '#FFFFFF', width: 160 }}
      />
      <Button type="submit" size="small" variant="contained" disabled={saving || !value.trim()} sx={{ borderRadius: 999, py: 0.5 }}>
        Save
      </Button>
      {error ? (
        <Typography variant="caption" color="error.main" sx={{ width: '100%' }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

function HintLink({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <Box component={Link} to={to} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'primary.main', fontWeight: 600, fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
      {icon}
      {label}
    </Box>
  );
}

/**
 * Google requires the photographer's credit with the photo. Plain text: the
 * photo sits inside a link to the trip, and links cannot nest.
 */
function Credit({ s, at = 'bottom' }: { s: Summary; at?: 'top' | 'bottom' | 'inline' }) {
  const who = s.trip.cover_photo_attribution?.[0];
  if (!who) return null;
  if (at === 'inline') {
    return (
      <Typography component="span" noWrap sx={{ display: 'block', mt: 0.75, fontSize: '0.6rem', opacity: 0.7 }}>
        Photo: {who.name}
      </Typography>
    );
  }
  return (
    <Typography
      component="span"
      sx={{
        position: 'absolute',
        right: 12,
        ...(at === 'top' ? { top: 10 } : { bottom: 8 }),
        fontSize: '0.62rem',
        color: 'rgba(255,255,255,0.8)',
        textShadow: '0 1px 2px rgba(0,0,0,0.6)',
        zIndex: 2,
      }}
    >
      Photo: {who.name}
    </Typography>
  );
}

/** The next trip, big: photo, title over it, and a floating "next up" card. */
function NextTripPhoto({ s, photo }: { s: Summary; photo: string | undefined }) {
  const t = s.trip;
  const arrival = t.start_mode
    ? `${t.start_mode === 'train' ? 'Train' : t.start_mode === 'flight' ? 'Flight' : t.start_mode[0].toUpperCase() + t.start_mode.slice(1)}${t.start_detail ? ` ${t.start_detail.split('·')[0].trim()}` : ''}`
    : 'How you get there: not decided';
  // Door to door: a trip that ends where it starts starts at home, so its first
  // leg is a departure, not a landing.
  const fromHome = !!t.start_place && t.start_place.trim().toLowerCase() === t.end_place?.trim().toLowerCase();
  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        component={Link}
        to={`/trips/${t.trip_id}`}
        aria-label={`Open ${t.title}`}
        sx={{
          position: 'relative',
          display: 'block',
          aspectRatio: { xs: '4 / 3.4', md: '4 / 3.6' },
          borderRadius: { xs: `${radius.card + 4}px`, md: '32px' },
          overflow: 'hidden',
          bgcolor: 'primary.main',
          boxShadow: elevation.photo,
          textDecoration: 'none',
        }}
      >
        {photo ? (
          <Box component="img" src={photo} alt={t.destination ?? t.title} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : null}
        <Box aria-hidden sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(8,18,13,0.62) 0%, rgba(8,18,13,0.18) 38%, rgba(0,0,0,0) 60%)' }} />
        <Box sx={{ position: 'absolute', left: { xs: 18, md: 28 }, right: { xs: 18, md: 28 }, top: { xs: 18, md: 26 }, color: '#FFFFFF' }}>
          <Box sx={{ display: 'inline-block', px: 1.5, py: 0.5, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.92)', fontSize: '0.78rem', fontWeight: 700, color: 'primary.main', mb: 1.5 }}>
            {countdown(s)}
          </Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, opacity: 0.85, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Next trip</Typography>
          <Typography sx={{ fontFamily: displayFont, fontWeight: 700, fontSize: { xs: '1.9rem', md: '2.6rem' }, lineHeight: 1.05, mt: 0.5 }}>
            {t.destination ?? t.title}
          </Typography>
          {s.start && s.end ? (
            <Typography sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, opacity: 0.9, mt: 0.75 }}>
              {formatDay(s.start)} – {formatDay(s.end)} · {s.nights} nights
            </Typography>
          ) : null}
        </Box>
        <Credit s={s} at="top" />
      </Box>

      {/* Floating "next up" card — the context-aware part of the home. On a
          phone it tucks under the photo; on desktop it floats off its corner. */}
      <Box
        sx={{
          position: { xs: 'relative', md: 'absolute' },
          mt: { xs: '-48px', md: 0 },
          mx: { xs: 1.75, md: 0 },
          left: { md: -40 },
          bottom: { md: -32 },
          width: { md: 300 },
          p: 2,
          borderRadius: `${radius.card}px`,
          bgcolor: '#FFFFFF',
          boxShadow: elevation.raised,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'rgba(26,58,42,0.08)', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
            {t.start_mode === 'train' ? <TrainOutlinedIcon /> : fromHome ? <FlightTakeoffIcon /> : <FlightLandIcon />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
              {arrival}
            </Typography>
            <Typography noWrap sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              {s.start ? formatDay(s.start) : ''}
              {shortTime(t.start_time) ? ` · ${fromHome ? 'leaves' : 'lands'} ${shortTime(t.start_time)}` : ''}
              {t.start_place ? ` · ${t.start_place}` : ''}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'text.secondary' }}>
            <span>
              {s.daysFilled} of {s.dayCount} days planned
            </span>
            {s.openChoices ? (
              <Box component="span" sx={{ color: 'secondary.dark', fontWeight: 700 }}>
                {s.openChoices} to decide
              </Box>
            ) : null}
          </Box>
          <Progress value={s.dayCount ? s.daysFilled / s.dayCount : 0} />
        </Box>
        <Button component={Link} to={`/trips/${t.trip_id}`} variant="contained" size="small" endIcon={<ArrowForwardIcon />} sx={{ borderRadius: 999, alignSelf: 'stretch' }}>
          Continue planning
        </Button>
      </Box>
    </Box>
  );
}

function EmptyCollage() {
  return (
    <Box
      aria-hidden
      sx={{
        aspectRatio: '4 / 3.4',
        borderRadius: '32px',
        background: 'linear-gradient(135deg, #1A3A2A 0%, #2C5643 55%, #C8522A 140%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {[480, 360, 240].map((size, i) => (
        <Box key={size} sx={{ position: 'absolute', right: -size / 3, top: -size / 4, width: size, height: size, borderRadius: '50%', border: `1px solid rgba(232,244,236,${0.14 - i * 0.03})` }} />
      ))}
      <Typography sx={{ ...serifVoice, position: 'absolute', left: 28, bottom: 26, right: 28, color: 'rgba(232,244,236,0.9)', fontSize: '1.2rem', lineHeight: 1.6 }}>
        Where you went. What it meant.
      </Typography>
    </Box>
  );
}

/* ── Trip tiles ───────────────────────────────────────────────────────────── */

function Progress({ value, light }: { value: number; light?: boolean }) {
  return (
    <Box sx={{ mt: 0.75, height: 6, borderRadius: 3, bgcolor: light ? 'rgba(255,255,255,0.3)' : '#EEE7DD', overflow: 'hidden' }} aria-hidden>
      <Box sx={{ height: '100%', width: `${Math.round(value * 100)}%`, borderRadius: 3, bgcolor: light ? '#FFFFFF' : 'primary.main' }} />
    </Box>
  );
}

function TripTile({ s, photo }: { s: Summary; photo: string | undefined }) {
  const t = s.trip;
  return (
    <Box
      component={Link}
      to={`/trips/${t.trip_id}`}
      sx={{
        position: 'relative',
        display: 'block',
        aspectRatio: '3 / 4',
        borderRadius: `${radius.card}px`,
        overflow: 'hidden',
        bgcolor: 'primary.main',
        color: '#FFFFFF',
        textDecoration: 'none',
        boxShadow: elevation.card,
        transition: 'transform 200ms ease, box-shadow 200ms ease',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: elevation.raised },
        '&:hover img': { transform: 'scale(1.04)' },
        '&:focus-visible': { outline: '3px solid', outlineColor: 'secondary.main', outlineOffset: 3 },
      }}
    >
      {photo ? (
        <Box component="img" src={photo} alt="" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 400ms ease' }} />
      ) : (
        <Box aria-hidden sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #2C5643, #1A3A2A)' }} />
      )}
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 45%, rgba(8,18,13,0.82) 100%)' }} />
      <Box sx={{ position: 'absolute', left: 12, top: 12, px: 1.25, py: 0.4, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.92)', color: 'primary.main', fontSize: '0.72rem', fontWeight: 700 }}>
        {countdown(s)}
      </Box>
      <Box sx={{ position: 'absolute', left: { xs: 12, md: 16 }, right: { xs: 12, md: 16 }, bottom: { xs: 12, md: 16 } }}>
        <Typography sx={{ fontFamily: displayFont, fontWeight: 700, fontSize: { xs: '1.15rem', md: '1.45rem' }, lineHeight: 1.1 }}>
          {t.destination ?? t.title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, fontSize: { xs: '0.72rem', md: '0.8rem' }, opacity: 0.9 }}>
          <CalendarTodayOutlinedIcon sx={{ fontSize: 13 }} />
          {s.start && s.end ? `${formatDay(s.start, { weekday: false })} – ${formatDay(s.end, { weekday: false })}` : 'Dates not set'}
        </Box>
        {s.route.length ? (
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, mt: 0.5, fontSize: '0.8rem', opacity: 0.9 }}>
            <PlaceOutlinedIcon sx={{ fontSize: 14 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.route.map((r) => r.town).join(' → ')}</span>
          </Box>
        ) : null}
        {s.dayCount && (s.daysAway === null || s.daysAway >= 0 || s.underway) ? (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', opacity: 0.9 }}>
              <span>
                {s.daysFilled}/{s.dayCount} days planned
              </span>
              {s.openChoices ? <span>{s.openChoices} open</span> : null}
            </Box>
            <Progress value={s.daysFilled / s.dayCount} light />
          </Box>
        ) : null}
        <Credit s={s} at="inline" />
      </Box>
    </Box>
  );
}

function NewTripTile() {
  return (
    <ButtonBase
      component={Link}
      to="/trips/new"
      sx={{
        aspectRatio: '3 / 4',
        borderRadius: `${radius.card}px`,
        border: '2px dashed',
        borderColor: 'rgba(26,58,42,0.22)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        color: 'primary.main',
        '&:hover': { bgcolor: 'rgba(26,58,42,0.04)', borderColor: 'primary.main' },
      }}
    >
      <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'primary.main', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AddIcon />
      </Box>
      <Typography sx={{ fontWeight: 700 }}>New trip</Typography>
    </ButtonBase>
  );
}

function HowItWorks() {
  const steps = [
    { n: '01', title: 'Start and finish', line: 'Just the dates. How you travel can wait until you know.' },
    { n: '02', title: 'Gather candidates', line: 'Research must-sees for your themes, or paste a list from ChatGPT.' },
    { n: '03', title: 'Plan the days', line: 'Roamoir fits them into the days you have. Keep or drop each suggestion.' },
  ];
  return (
    <Box component="section" aria-labelledby="how">
      <Typography id="how" component="h2" sx={{ fontFamily: displayFont, fontWeight: 700, fontSize: { xs: '1.6rem', md: '2.2rem' }, mb: 3 }}>
        How it works
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2.5 }}>
        {steps.map((s) => (
          <Box key={s.n} sx={{ p: 3, borderRadius: `${radius.card}px`, bgcolor: '#FFFFFF', boxShadow: elevation.card }}>
            <Typography sx={{ fontFamily: displayFont, fontStyle: 'italic', fontSize: '1.6rem', color: 'secondary.main' }}>{s.n}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', mt: 1 }}>{s.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {s.line}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
